import { useState, useRef, useCallback, useEffect } from "react";

export default function useExerciseEngine({
  questions = [],
  showResult = false,
  onComplete = () => {},
  externalAnswers = {},
  normalize = (v, cap) => {
    let t = String(v || "").trim();
    if (!cap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  },
}) {
  const [answers, setAnswers] = useState({});
  const startTimeRef = useRef(Date.now());
  const prevExternalRef = useRef({});

  /* ================= SAFE SYNC WITH PARENT ================= */
  useEffect(() => {
    if (!externalAnswers || typeof externalAnswers !== "object") return;

    const isSame =
      JSON.stringify(prevExternalRef.current) ===
      JSON.stringify(externalAnswers);

    if (isSame) return;

    prevExternalRef.current = externalAnswers;
    setAnswers((prev) => {
  return JSON.stringify(prev) === JSON.stringify(externalAnswers)
    ? prev
    : externalAnswers;
});

  }, [externalAnswers]);

  /* ================= SET ANSWER ================= */
  const setAnswer = useCallback((key, value) => {
    setAnswers((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  /* ================= TOTAL ITEMS ================= */
  const getTotalItems = useCallback(() => {
    let total = 0;

    questions.forEach((q) => {
      if (q.type === "fill") {
        total += q.answers?.length || 0;
      } else {
        total += 1;
      }
    });

    return total;
  }, [questions]);

  /* ================= ALL ANSWERED ================= */
  const isAllAnswered = useCallback(() => {
    return Object.keys(answers).length >= getTotalItems();
  }, [answers, getTotalItems]);

  /* ================= EVALUATE ================= */
  const evaluate = useCallback(() => {
    let correctCount = 0;
    let total = 0;
    const skillMap = {};

    questions.forEach((q, qIdx) => {
      const skill = q.skill || "general";

      if (q.type === "fill") {
        (q.answers || []).forEach((correctAns, bIdx) => {
          total++;

          // ✅ match key pattern from component
          const key = Object.keys(answers).find((k) =>
            k.endsWith(`${qIdx}-${bIdx}`)
          );

          const user = normalize(answers[key], q.requireCapitalization);
          const correct = normalize(correctAns, q.requireCapitalization);

          const isCorrect = user === correct;
          if (isCorrect) correctCount++;

          if (!skillMap[skill]) {
            skillMap[skill] = { correct: 0, total: 0 };
          }

          skillMap[skill].total++;
          if (isCorrect) skillMap[skill].correct++;
        });
      } else {
        total++;

        const user = normalize(answers[qIdx], q.requireCapitalization);
        const correct = normalize(q.answer, q.requireCapitalization);

        const isCorrect = user === correct;
        if (isCorrect) correctCount++;

        if (!skillMap[skill]) {
          skillMap[skill] = { correct: 0, total: 0 };
        }

        skillMap[skill].total++;
        if (isCorrect) skillMap[skill].correct++;
      }
    });

    const percent =
      total > 0 ? Math.round((correctCount / total) * 100) : 0;

    const timeSpent = Math.floor(
      (Date.now() - startTimeRef.current) / 1000
    );

    return {
      scorePercent: percent,
      correctAnswers: correctCount,
      totalQuestions: total,
      timeSpent,
      skills: Object.fromEntries(
        Object.entries(skillMap).map(([k, v]) => [
          k,
          Math.round((v.correct / v.total) * 100),
        ])
      ),
    };
  }, [answers, questions, normalize]);

  /* ================= SUBMIT ================= */
  const submit = useCallback(() => {
    const result = evaluate();

    onComplete({
      answer: answers,
      ...result,
    });

    return result;
  }, [evaluate, answers, onComplete]);

  /* ================= OPTION STATE ================= */
  const getOptionState = useCallback(
    (selected, option, correctAnswer) => {
      const isSelected = selected === option;
      const isCorrect =
        normalize(option) === normalize(correctAnswer);

      return {
        isSelected,
        isCorrect,
        className: [
          isSelected && !showResult && "bg-accent",
          showResult &&
            isSelected &&
            isCorrect &&
            "bg-green-100 border-green-400",
          showResult &&
            isSelected &&
            !isCorrect &&
            "bg-red-100 border-red-400",
        ]
          .filter(Boolean)
          .join(" "),
      };
    },
    [normalize, showResult]
  );

  /* ================= INPUT STATE ================= */
  const getInputState = useCallback(
    (value, correctAnswer) => {
      const isCorrect =
        normalize(value) === normalize(correctAnswer);

      return {
        isCorrect,
        className: [
          showResult && value && isCorrect && "bg-green-100 border-green-400",
          showResult && value && !isCorrect && "bg-red-100 border-red-400",
        ]
          .filter(Boolean)
          .join(" "),
      };
    },
    [normalize, showResult]
  );

  /* ================= RESET ================= */
  const reset = useCallback(() => {
    setAnswers({});
    startTimeRef.current = Date.now();
  }, []);

  return {
    answers,
    setAnswer,
    submit,
    getOptionState,
    getInputState,
    isAllAnswered,
    reset,
  };
}